import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { matchSorter } from 'match-sorter';
import { Column, Page, PageEmptyState, PageErrorState, Row, Searchbox, TableList, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { EmptyStateIllustration } from '~/components/EmptyStateIllustration/EmptyStateIllustration';
import PageLoadingState from '~/components/PageLoadingState';
import { useContactSummaries } from '~/hooks/reactQuery/queries/useContactSummaries';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { SEARCHABLE_KEYS, SORT_IDS, sortContacts } from './ContactsDashboard.utils';
import { ContactsDashboardRow } from './ContactsDashboardRow';
import { useAddContactToClientModal } from './hooks/useAddContactToClientModal';
import { useDeleteContactModal } from './hooks/useDeleteContactModal';
import { useEditContactIdentityModal } from './hooks/useEditContactIdentityModal';
import { useOpenContactClientPortal } from './hooks/useOpenContactClientPortal';
const columnDefs = [
    {
        width: [
            140,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_shared_contacts_by_name'
        }
    },
    {
        width: [
            160,
            300
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_shared_contacts_by_email'
        }
    },
    {
        width: [
            140,
            200
        ],
        sortable: true,
        mobile: {},
        bi: {
            name: 'sort_shared_contacts_by_phone'
        }
    },
    {
        width: [
            200,
            400
        ],
        mobile: {}
    }
];
const DEFAULT_SORT = {
    colIndex: 0,
    sort: 'asc'
};
export const GetContactsDashboardTID = (parentTestId)=>getTestIds(parentTestId);
export const ContactsDashboard = ()=>{
    const { ContactsDashboardTestIds } = useMemo(()=>GetContactsDashboardTID(), []);
    const { t } = useLocaleKeys('pages.contactsDashboard');
    const [search, setSearch] = useState('');
    const { contacts, isLoading, error, refresh } = useContactSummaries();
    const { sort, changeSort } = useSortFromUrl({
        defaultSort: DEFAULT_SORT,
        sortIds: SORT_IDS
    });
    const openEditModal = useEditContactIdentityModal(ContactsDashboardTestIds.EditModal());
    const openDeleteModal = useDeleteContactModal(ContactsDashboardTestIds.DeleteModal());
    const openClientPortal = useOpenContactClientPortal();
    const openAddToClientModal = useAddContactToClientModal(ContactsDashboardTestIds.AddToClientModal());
    useSetDocumentTitle(t.title());
    const visibleContacts = useMemo(()=>{
        const matched = search.length === 0 ? contacts : matchSorter(contacts, search, {
            keys: SEARCHABLE_KEYS
        });
        return sortContacts(matched, sort);
    }, [
        contacts,
        search,
        sort
    ]);
    const headerItems = useMemo(()=>[
            {
                id: SORT_IDS[0],
                value: t.columns.contactName()
            },
            {
                id: SORT_IDS[1],
                value: t.columns.email()
            },
            {
                id: SORT_IDS[2],
                value: t.columns.phone()
            },
            {
                id: 'associatedClients',
                value: t.columns.associatedClients()
            }
        ], []);
    if (isLoading) return /*#__PURE__*/ _jsx(PageLoadingState, {});
    if (error != null) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: ContactsDashboardTestIds.ErrorState(),
            title: t.errorState.title(),
            message: t.errorState.description(),
            onRefresh: refresh,
            pullToRefresh: true
        });
    }
    const hasNoContacts = contacts.length === 0;
    return /*#__PURE__*/ _jsx(Page, {
        testId: ContactsDashboardTestIds.Root(),
        onRefresh: refresh,
        children: /*#__PURE__*/ _jsx(TableList.Provider, {
            columnDefs: columnDefs,
            hasActionColumn: true,
            sort: sort,
            onSortChange: changeSort,
            children: /*#__PURE__*/ _jsxs(Column, {
                gap: 16,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "h5",
                        weight: "normal",
                        children: t.title()
                    }),
                    /*#__PURE__*/ _jsx(Row, {
                        children: /*#__PURE__*/ _jsx(Searchbox, {
                            testId: ContactsDashboardTestIds.Search(),
                            placeholder: t.searchPlaceholder(),
                            value: search,
                            onChange: (event)=>setSearch(event.target.value),
                            onClear: ()=>setSearch(''),
                            bi: {
                                name: 'contacts_search'
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Column, {
                        children: [
                            /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: ContactsDashboardTestIds.TableHeader()
                            }),
                            hasNoContacts && /*#__PURE__*/ _jsx(PageEmptyState, {
                                maxHeight: true,
                                testId: ContactsDashboardTestIds.EmptyState(),
                                illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                                title: t.emptyState.title(),
                                message: t.emptyState.description()
                            }),
                            !hasNoContacts && visibleContacts.length === 0 && /*#__PURE__*/ _jsx(PageEmptyState, {
                                maxHeight: true,
                                testId: ContactsDashboardTestIds.NoSearchResults(),
                                illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                                title: t.noSearchResults.title(),
                                message: t.noSearchResults.description({
                                    search
                                })
                            }),
                            visibleContacts.length > 0 && /*#__PURE__*/ _jsx(Column, {
                                testId: ContactsDashboardTestIds.Rows(),
                                children: visibleContacts.map((contact)=>/*#__PURE__*/ _jsx(ContactsDashboardRow, {
                                        contact: contact,
                                        testId: ContactsDashboardTestIds.Row(contact.contactId),
                                        onEdit: openEditModal,
                                        onAddToAnotherClient: openAddToClientModal,
                                        onDelete: openDeleteModal,
                                        onViewClientPortal: openClientPortal
                                    }, contact.contactId))
                            })
                        ]
                    })
                ]
            })
        })
    });
};
